import { useState, useEffect } from "react";
import { Modal } from "../../ui/modal";
import { createCryptoWallet } from "../API/services";
import Alert from "../../ui/alert/Alert";
import { Bitcoin } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateCryptoWalletModal = ({ isOpen, onClose, onCreated }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleCreate = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      await createCryptoWallet();

      setSuccessMessage("Stable coin Wallet created successfully.");

      setTimeout(() => {
        onClose();
        onCreated();
      }, 800);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to create Stable coin Wallet");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6
        dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
        <Bitcoin className="h-6 w-6 text-amber-600 dark:text-amber-400" />
      </div>

      <h2 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
        Create Stable coin Wallet
      </h2>

      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        This creates a Stable coin Wallet you can use to receive and send cryptocurrency.
      </p>

      {successMessage && (
        <div className="mb-4">
          <Alert variant="success" title="Success" message={successMessage} />
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          disabled={submitting}
          className="rounded-lg border border-gray-300 bg-white px-4 py-3
            text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50
            dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
        >
          Cancel
        </button>

        <button
          onClick={handleCreate}
          disabled={submitting}
          className="rounded-lg border border-gray-300 bg-white px-4 py-3
            text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50
            dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
        >
          {submitting ? "Creating..." : "Create Wallet"}
        </button>
      </div>
    </Modal>
  );
};

export default CreateCryptoWalletModal;